import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FiX } from 'react-icons/fi';
import { getAdsMediaDisplayUrl, getAdsVideoSrc } from '../utils/adsMediaUrl';
import { formatAdsDate, hasAdsText } from '../utils/adsClicks';

const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'ngrok-skip-browser-warning': 'true'
});

const ViewAdsModal = ({ ad, onClose }) => {
    const [adDetail, setAdDetail] = useState(ad || null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [mediaLightbox, setMediaLightbox] = useState(null);
    const [videoDirectFallback, setVideoDirectFallback] = useState({});

    useEffect(() => {
        if (!ad?._id) return;

        const base = `${process.env.REACT_APP_API_URL}/api/ads/${ad._id}`;
        let cancelled = false;

        setLoading(true);
        setError('');

        axios.get(base, { headers: authHeaders() })
            .then((res) => {
                if (!cancelled && res.data.success) {
                    setAdDetail(res.data.data);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setError(err.response?.data?.error || err.message);
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [ad?._id]);

    const displayAd = adDetail || ad;

    const createdLabel = formatAdsDate(displayAd?.createdAt);
    const updatedLabel = formatAdsDate(displayAd?.updatedAt);
    const sourceRows = (displayAd?.adsSource || []).filter(
        (row) => hasAdsText(row.source) || hasAdsText(row.link)
    );

    const openMediaLightbox = (type, url, adId = null) => {
        setMediaLightbox({
            type,
            url,
            adId,
            useDirect: false
        });
    };

    const renderMediaThumb = (type, url, adId = null) => {
        const isVideo = type === 'video';
        const mediaType = type === 'gif' ? 'adsGif' : type === 'video' ? 'adsVideos' : 'adsImage';
        const commonClass = 'w-28 h-28 object-cover rounded border border-gray-200 cursor-pointer hover:ring-2 hover:ring-blue-400 bg-black';
        const displaySrc = isVideo
            ? getAdsVideoSrc(url, adId, Boolean(videoDirectFallback[url]))
            : getAdsMediaDisplayUrl(url, mediaType, adId);

        if (isVideo) {
            return (
                <video
                    key={`${url}-${videoDirectFallback[url] ? 'direct' : 'proxy'}`}
                    src={displaySrc}
                    className={commonClass}
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="auto"
                    onClick={() => openMediaLightbox('video', url, adId)}
                    onError={() => {
                        if (!videoDirectFallback[url]) {
                            setVideoDirectFallback((prev) => ({ ...prev, [url]: true }));
                        }
                    }}
                />
            );
        }

        return (
            <img
                key={url}
                src={url}
                alt=""
                className={commonClass}
                onClick={() => openMediaLightbox(type === 'gif' ? 'gif' : 'image', url, adId)}
            />
        );
    };

    const renderMediaSection = (title, items, type, adId = null) => {
        if (!items?.length) return null;
        return (
            <section className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-lg font-medium mb-2">{title}</h4>
                <div className="flex flex-wrap gap-2">
                    {items.map((url) => renderMediaThumb(type, url, adId))}
                </div>
            </section>
        );
    };

    const infoFields = [
        hasAdsText(displayAd?.adsCategory) && {
            label: 'Category',
            value: displayAd.adsCategory.trim()
        },
        createdLabel && { label: 'Created', value: createdLabel },
        updatedLabel && { label: 'Updated', value: updatedLabel }
    ].filter(Boolean);

    return (
        <>
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                <div className="relative top-20 mx-auto p-5 border max-w-5xl w-full shadow-lg rounded-md bg-white">
                    <div className="flex justify-between items-center border-b pb-4 mb-4">
                        <h3 className="text-xl font-medium">
                            Ad Details -{' '}
                            <span className="text-blue-700 font-bold">#{displayAd?.adsID ?? ad?.adsID}</span>
                        </h3>
                        <button
                            type="button"
                            onClick={onClose}
                            className="text-gray-500 hover:text-gray-700"
                            aria-label="Close"
                        >
                            <FiX className="w-6 h-6" />
                        </button>
                    </div>

                    {error && (
                        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md text-sm">
                            {error}
                        </div>
                    )}

                    {loading && !adDetail ? (
                        <p className="text-gray-600 py-8 text-center">Loading ad details...</p>
                    ) : (
                        <div className="space-y-6 text-sm">
                            {infoFields.length > 0 && (
                                <section className="bg-gray-50 p-4 rounded-lg grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {infoFields.map((field) => (
                                        <div key={field.label}>
                                            <span className="font-semibold text-gray-700">{field.label}:</span>{' '}
                                            <span className="text-blue-700">{field.value}</span>
                                        </div>
                                    ))}
                                </section>
                            )}

                            {renderMediaSection('Images', displayAd?.adsImage, 'image', displayAd?._id)}
                            {renderMediaSection('GIFs', displayAd?.adsGif, 'gif', displayAd?._id)}
                            {renderMediaSection('Videos', displayAd?.adsVideos, 'video', displayAd?._id)}

                            {sourceRows.length > 0 && (
                                <section className="bg-gray-50 p-4 rounded-lg">
                                    <h4 className="text-lg font-medium mb-3">Sources & Links</h4>
                                    <table className="min-w-full border border-gray-200 rounded-md overflow-hidden">
                                        <thead className="bg-gray-100">
                                            <tr>
                                                <th className="px-3 py-2 text-left">Source</th>
                                                <th className="px-3 py-2 text-left">Link</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {sourceRows.map((row, index) => (
                                                <tr key={row._id || index}>
                                                    <td className="px-3 py-2">
                                                        {hasAdsText(row.source) ? row.source : null}
                                                    </td>
                                                    <td className="px-3 py-2 break-all">
                                                        {hasAdsText(row.link) ? (
                                                            <a
                                                                href={row.link}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="text-blue-600 hover:underline"
                                                            >
                                                                {row.link}
                                                            </a>
                                                        ) : null}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </section>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {mediaLightbox && (
                <div
                    className="fixed inset-0 z-[150] flex items-center justify-center bg-black/80 p-4"
                    onClick={() => setMediaLightbox(null)}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Media preview"
                >
                    <button
                        type="button"
                        onClick={() => setMediaLightbox(null)}
                        className="absolute top-4 right-4 p-2 rounded-full bg-white/90 text-gray-800 hover:bg-white z-10"
                        aria-label="Close preview"
                    >
                        <FiX className="w-6 h-6" />
                    </button>
                    <div className="max-w-full max-h-full flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                        {mediaLightbox.type === 'video' ? (
                            <video
                                key={mediaLightbox.useDirect ? 'direct' : 'proxy'}
                                src={mediaLightbox.useDirect
                                    ? mediaLightbox.url
                                    : getAdsVideoSrc(mediaLightbox.url, mediaLightbox.adId, mediaLightbox.useDirect)}
                                className="max-w-full max-h-[90vh] rounded shadow-lg"
                                autoPlay
                                loop
                                muted
                                playsInline
                                controls
                                onError={() => {
                                    if (!mediaLightbox.useDirect) {
                                        setMediaLightbox((prev) => prev && ({ ...prev, useDirect: true }));
                                    }
                                }}
                            />
                        ) : (
                            <img
                                key={mediaLightbox.url}
                                src={getAdsMediaDisplayUrl(
                                    mediaLightbox.url,
                                    mediaLightbox.type === 'gif' ? 'adsGif' : 'adsImage',
                                    mediaLightbox.adId
                                )}
                                alt=""
                                className="max-w-full max-h-[90vh] object-contain rounded shadow-lg"
                            />
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default ViewAdsModal;
